import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Snackbar, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Public as PublicIcon, LinkOff as RevokeIcon,
} from '@mui/icons-material';
import menuShareService from '../../services/menuShareService';
import type { MenuShare } from '../../types/menu';

interface Props {
  venueId: string;
  menuId: string;
  /** Menu.status — drives the DRAFT warning. */
  menuStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
}

/**
 * Publish / share-link manager for a menu.
 *
 * The public unified resolver (/m/:slug and /m/:venueDomain/:slug) renders an
 * ACTIVE menu with an active share. So the customer-facing URL is LIVE once the
 * menu is Active; for DRAFT/ARCHIVED menus it resolves to a "not available"
 * page. Wording below reflects that (MENU-PUBLIC-URL-WARNING-FIX.1).
 */
const MenuPublishPanel: React.FC<Props> = ({ venueId, menuId, menuStatus }) => {
  const theme = useTheme();
  const [share, setShare] = useState<MenuShare | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    menuShareService.getShare(venueId, menuId)
      .then(setShare)
      .catch((err: any) => {
        // 404 → no active share yet. That's a normal state, not an error.
        if (err?.response?.status === 404) {
          setShare(null);
        } else {
          setError('Could not load publish status. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [venueId, menuId]);

  // Public URL for the menu. Live once the menu is ACTIVE (see isLive below).
  // MENU-PUBLIC-URL-SLUGS.1 — canonical form is venue-scoped
  // (/m/<venueDomain>/<slug>) when the venue has a public domain; otherwise
  // fall back to the legacy /m/<slug> form.
  const reservedUrl = share
    ? share.venueDomain
      ? `${window.location.origin}/m/${encodeURIComponent(share.venueDomain)}/${encodeURIComponent(share.slug)}`
      : `${window.location.origin}/m/${encodeURIComponent(share.slug)}`
    : null;

  const handlePublish = async () => {
    setWorking(true);
    setError('');
    try {
      const created = await menuShareService.createOrGetShare(venueId, menuId);
      setShare(created);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not publish. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleRevoke = async () => {
    setWorking(true);
    setError('');
    try {
      await menuShareService.revokeShare(venueId, menuId);
      setShare(null);
      setConfirmRevoke(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not unpublish. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyToast(true);
    } catch {
      window.prompt('Copy the value:', value);
    }
  };

  // Public URL is live only for an ACTIVE menu with an active share; DRAFT and
  // ARCHIVED menus resolve to a "not available" page.
  const isLive = !!share && menuStatus === 'ACTIVE';

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
          <PublicIcon sx={{ color: share ? 'success.main' : 'text.disabled', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {share ? (isLive ? 'Public menu is live' : 'Public URL reserved') : 'Not published'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {share
                ? (isLive
                    ? 'Customers can open the public URL below.'
                    : 'A public URL is reserved. It goes live once this menu is set to Active.')
                : 'Publish this menu to reserve its public URL. It goes live once the menu is Active.'}
            </Typography>
          </Box>
        </Box>

        {!share && !loading && (
          <Button
            variant="contained"
            size="small"
            disabled={working}
            onClick={handlePublish}
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
          >
            {working ? <CircularProgress size={16} color="inherit" /> : 'Publish Menu'}
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mt: 1.5, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {/* Status advisory — the public URL only resolves for an ACTIVE menu.
          Shown only when a share exists but the menu is not yet ACTIVE. */}
      {share && menuStatus === 'DRAFT' && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 1 }}>
          This menu is <strong>DRAFT</strong>, so the public URL returns a "not available"
          page. Set the menu to <strong>ACTIVE</strong> to make it live.
        </Alert>
      )}
      {share && menuStatus === 'ARCHIVED' && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 1 }}>
          This menu is <strong>ARCHIVED</strong>, so the public URL returns a "not available"
          page until the menu is restored to <strong>ACTIVE</strong>.
        </Alert>
      )}

      {share && (
        <Box sx={{ mt: 2 }}>
          {/* Slug field */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Slug
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, mb: 1.5 }}>
            <TextField
              value={share.slug}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    backgroundColor: alpha(theme.palette.text.primary, 0.03),
                  },
                },
              }}
              onFocus={e => e.target.select()}
            />
            <Tooltip title="Copy slug">
              <IconButton size="small" onClick={() => handleCopy(share.slug)} aria-label="Copy slug">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Public URL field (live when the menu is ACTIVE). */}
          {reservedUrl && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {isLive ? 'Public URL' : 'Public URL (goes live when Active)'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <TextField
                  value={reservedUrl}
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      readOnly: true,
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        backgroundColor: alpha(theme.palette.text.primary, 0.03),
                        color: theme.palette.text.primary,
                      },
                    },
                  }}
                  onFocus={e => e.target.select()}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Copy reserved URL">
                    <IconButton size="small" onClick={() => handleCopy(reservedUrl)} aria-label="Copy reserved URL">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Unpublish">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={working}
                      onClick={() => setConfirmRevoke(true)}
                      aria-label="Unpublish"
                    >
                      <RevokeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Revoke confirmation */}
      <Dialog open={confirmRevoke} onClose={() => !working && setConfirmRevoke(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Unpublish menu?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The public URL will stop resolving immediately. Anyone who has saved the link or
            scanned its code will see a "not available" page. Re-publishing later issues a new URL.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRevoke(false)} disabled={working} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevoke}
            disabled={working}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {working ? <CircularProgress size={18} color="inherit" /> : 'Unpublish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyToast}
        autoHideDuration={2000}
        onClose={() => setCopyToast(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default MenuPublishPanel;
